import { useEffect, useState } from 'react';
import { useMediaQuery, Box, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { RootState } from '../../redux/store';
import { CustomerInfo } from 'orderme-api-integration-client';
import { NavBar } from '../appBars/navBar/navBar';
import { PasswordChange } from '../auth/accountSettings/passwordChange';
import { CartInfo } from './cartInfo';
import { LoginDialog } from '../auth/login/loginDialog';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { BottomCartBar } from '../appBars/cartBar/bottomCartBar';
import { appStyleMode } from '../../helpers/clientConfigs';
import { AppStyle } from '../../helpers/appStyle';
import { StickyCartBar } from '../appBars/cartBar/stickyCartBar';
import { CartServicesList } from './cartServicesList';
import { CartServicesEmpty } from './cartServicesEmpty';
import { BreadcrumbsBar } from '../breadcrumbs/breadcrumbsBar';
import { useRouteNavigator } from '../../routes/useRouteNavigator';
import { CartRoute } from '../../routes/allRoutes';
import LoginRegister from './loginRegister';
import { trackersRunner } from '../../integrations/trackersRunner';
import { loadCartAsync } from '../../redux/reducers/shoppingCartReducer';

export function ShoppingCart() {
  const dispatch = useAppDispatch();
  const routeNavigator = useRouteNavigator();
  const shoppingCartItems = useAppSelector((state: RootState) => state.shoppingCartState.items);
  const [openPasswordChange, setOpenPasswordChange] = useState(false);
  const isPasswordRestored: boolean = useAppSelector((state: RootState) => state.authState.isPasswordRestored);
  const isUserLoggedIn: boolean = useAppSelector((state: RootState) => state.authState.isLoggedIn);
  const isUserLoggedInAnonymous: boolean = useAppSelector(
    (state: RootState) => state.authState.anonymousLoginInfo.isLoggedIn
  );
  const userInfo: CustomerInfo = useAppSelector((state: RootState) => state.customerState.customerInfo);
  const [openLogin, setOpenLogin] = useState(false);
  const { t } = useTranslation();
  const useTwoColumns = useMediaQuery('(min-width:1200px)');

  useEffect(() => {
    dispatch(loadCartAsync());
  }, [dispatch]);

  const buy = () => {
    if (isPasswordRestored) {
      showPasswordChange();
      return;
    }

    if (!isUserLoggedIn) {
      if (appStyleMode === AppStyle.Mobile) {
        setOpenLogin(true);
        return;
      }

      if (!isUserLoggedInAnonymous) {
        setOpenLogin(true);
        return;
      }
    }

    trackersRunner.startedCheckout();

    if (userInfo.ordersUsingAddressesOnly) {
      routeNavigator.goToAddressBook({ state: true });
    } else {
      routeNavigator.goToWhereTo();
    }
  };

  const showPasswordChange = () => {
    setOpenPasswordChange(true);
  };

  const closePasswordChange = () => {
    setOpenPasswordChange(false);
  };

  const closeLogin = () => {
    setOpenLogin(false);
  };

  const websiteServicesGrid = () => {
    return (
      <Grid
        container={useTwoColumns}
        sx={{
          justifyContent: 'center',
          flexDirection: useTwoColumns ? 'row' : 'column',
          width: '100%',
        }}
      >
        <Grid size={{ lg: 8, xs: 7 }}>
          <CartServicesList cartItems={shoppingCartItems} />
          <Box sx={{ p: '5px 15px 15px 15px' }}>{!isUserLoggedIn && <LoginRegister />}</Box>
        </Grid>
        <Grid
          size="grow"
          sx={{
            position: 'sticky',
            top: 0,
            mt: useTwoColumns ? '16px' : '0px',
            height: '100%',
            width: '100%',
          }}
        >
          <Box sx={{ mx: '16px' }}>
            <CartInfo />
          </Box>
          {useTwoColumns && (
            <StickyCartBar barText={t('payment:continue')} sx={{ mx: '17px', mb: '10px' }} open={buy} />
          )}
        </Grid>
      </Grid>
    );
  };

  const mobileServicesGrid = () => {
    return (
      <Stack sx={{ justifyContent: 'center', width: '100%' }}>
        <Box sx={{ px: '5px' }}>
          <CartServicesList cartItems={shoppingCartItems} />
        </Box>
        <Box sx={{ px: '20px' }}>
          <CartInfo />
        </Box>
      </Stack>
    );
  };

  return (
    <Stack sx={{ alignItems: 'center' }}>
      <NavBar barTitle={t('payment:cart')} open={() => routeNavigator.goHome()} />
      <BreadcrumbsBar sx={{ maxWidth: '100%' }} paths={[{ path: CartRoute }]} />
      <Box
        className={appStyleMode === AppStyle.Mobile && 'shopping-cart'}
        sx={{
          backgroundColor: 'white',
          color: 'black',
          borderRadius: '15px',
          width: '100%',
        }}
      >
        <Stack sx={{ justifyContent: 'center', alignItems: 'center' }}>
          {shoppingCartItems.length === 0 ? (
            <CartServicesEmpty />
          ) : appStyleMode === AppStyle.Mobile ? (
            mobileServicesGrid()
          ) : (
            websiteServicesGrid()
          )}
        </Stack>

        {appStyleMode === AppStyle.Mobile ? (
          <BottomCartBar barText={t('payment:continue')} open={buy} isDisabled={shoppingCartItems.length < 1} />
        ) : (
          !useTwoColumns && (
            <StickyCartBar barText={t('payment:continue')} open={buy} isDisabled={shoppingCartItems.length < 1} />
          )
        )}

        <PasswordChange open={openPasswordChange} onClose={closePasswordChange} />
        <LoginDialog open={openLogin} onClose={() => closeLogin()} allowAnonymous />
      </Box>
    </Stack>
  );
}
